import type { AggregatedIngredient, RecipeStep } from './types';
import { type Amount, type Unit, baseUnit, toBaseQuantity, unitDimension, unitGroup } from './units';

export interface IngredientEntry {
  name: string;
  quantity: number | null;
  unit: Unit | null;
  note?: string | null;
}

/** Normalised name used to recognise the same ingredient across steps and recipes. */
export function ingredientKey(name: string): string {
  return name.normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Every ingredient line from the add-ingredients steps, in order. */
export function ingredientLines(steps: readonly RecipeStep[]): IngredientEntry[] {
  return steps.flatMap((step) => (step.type === 'add_ingredients' ? step.ingredients : []));
}

interface Bucket {
  unit: Unit | null;
  /** Sum in `unit` while every line used the same unit. */
  total: number;
  /** Sum in grams/millilitres, used once units are mixed. */
  base: number;
  mixed: boolean;
}

/**
 * Adds up ingredient lines by ingredient: "1 tbsp butter" + "2 tbsp butter"
 * becomes "3 tbsp butter", and "1 cup milk" + "100 ml milk" becomes 337 ml.
 * Weights and volumes can't be combined without densities, so an ingredient
 * may end up with several amounts. First-seen order is preserved.
 */
export function aggregateIngredients(entries: Iterable<IngredientEntry>, scale = 1): AggregatedIngredient[] {
  const groups = new Map<
    string,
    { name: string; buckets: Map<string, Bucket>; unmeasured: boolean; notes: Set<string> }
  >();

  for (const entry of entries) {
    const key = ingredientKey(entry.name);
    if (!key) continue;
    let group = groups.get(key);
    if (!group) {
      group = { name: entry.name.trim(), buckets: new Map(), unmeasured: false, notes: new Set() };
      groups.set(key, group);
    }
    const note = entry.note?.trim();
    if (note) group.notes.add(note);
    if (entry.quantity === null || !(entry.quantity > 0)) {
      group.unmeasured = true;
      continue;
    }

    const amount = { quantity: entry.quantity * scale, unit: entry.unit };
    const bucketKey = unitGroup(entry.unit);
    const bucket = group.buckets.get(bucketKey);
    if (!bucket) {
      group.buckets.set(bucketKey, {
        unit: entry.unit,
        total: amount.quantity,
        base: toBaseQuantity(amount),
        mixed: false,
      });
    } else {
      bucket.base += toBaseQuantity(amount);
      if (bucket.unit === entry.unit) bucket.total += amount.quantity;
      else bucket.mixed = true;
    }
  }

  return Array.from(groups, ([key, group]) => ({
    key,
    name: group.name,
    amounts: Array.from(group.buckets.values(), (bucket): Amount => {
      if (!bucket.mixed) return { quantity: bucket.total, unit: bucket.unit };
      const dimension = unitDimension(bucket.unit);
      return { quantity: bucket.base, unit: dimension ? baseUnit(dimension) : bucket.unit };
    }),
    unmeasured: group.unmeasured,
    notes: [...group.notes],
  }));
}

/** Turns aggregated ingredients back into entries, e.g. to combine several recipes into one list. */
export function aggregatedEntries(items: readonly AggregatedIngredient[], scale = 1): IngredientEntry[] {
  return items.flatMap((item) => {
    const entries: IngredientEntry[] = item.amounts.map((amount) => ({
      name: item.name,
      quantity: amount.quantity * scale,
      unit: amount.unit,
    }));
    if (item.unmeasured || entries.length === 0) entries.push({ name: item.name, quantity: null, unit: null });
    return entries;
  });
}
